import { icons, Images, LucideProps } from 'lucide-react';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AColors } from '../../atoms/colors/colors';
import { SelectIconContainer } from '../../containers_v2/forms/PopupField';
import { FlexDiv } from '../../containers_v2/products/style';
import CloseIcon from '../../styles/custom-icons/close';
import { Dot } from '../../styles/global/css/Dot';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { PrimaryColor } from '../../styles/global/css/Utils';
import { Translate } from '../../styles/global/translate';
import { colorGradient } from '../avatar/Data/ColorList';
import Dropdown from './Dropdown';
import { DropdownPropsNoData } from './model/Model';

const DEFAULT_COLUMNS = 4;
const DEFAULT_ROWS = 3;
const COLOR_COLUMNS = 6;
const COLOR_ROWS = 7;
const COLOR_OPTION_DOT_SIZE = 22;

export default function DropdownColor(props: DropdownPropsNoData<string, false, false> & {
	optionDotSize?: number,
	dotSize?: string,
	optionRows?: number,
	optionCols?: number,
	optionPadding?: string,
	selectedValue?: string,
	restrictedColors?: boolean,
}) {
	const restrictedColors = useRecoilValue(AColors);
	const optionDotSize = props.optionDotSize ?? COLOR_OPTION_DOT_SIZE;
	const colorsData = React.useMemo(() => (props.restrictedColors ? restrictedColors.map(c => c.colorCode) : colorGradient).map(value => ({ label: <Dot color={value} size={optionDotSize + 'px'} cursor='pointer' />, value })), [props.restrictedColors, optionDotSize]);
	return <Dropdown<string>
		{...props}
		searchable={false}
		gallery
		selectedValue={props.selectedValue ? colorsData.find(c => c.value === props.selectedValue) : undefined}
		JSXButton={props.JSXButton ? props.JSXButton : ({ value }) => <Dot color={value?.value} empty={!value} size={props.dotSize ?? '35px'} cursor='pointer' />}
		dropdownStyle={{
			...props.dropdownStyle,
			optionHeight: props.dropdownStyle?.optionHeight ?? ((props.optionRows ?? COLOR_ROWS) * (optionDotSize + 22)) + 'px',
			optionWidth: props.dropdownStyle?.optionWidth ?? ((props.optionCols ?? COLOR_COLUMNS) * (optionDotSize + 22)) + 'px',
			optionPadding: props.optionPadding
		}}
		datalist={colorsData}
	/>;
}

export const iconNameList = [
	'Bell',
	'FileText',
	'Heart',
	'Star',
	'ThumbsUp',
	'ThumbsDown',
	'Medal',
	'Coffee',
	'CookingPot',
	'Utensils',
	'ChefHat',
	'Martini',
	'Wine',
	'Palette',
	'PaintRoller',
	'GraduationCap',
	'Notebook',
	'Flame',
	'Leaf',
	'Briefcase',
	'UserRound',
	'ShoppingCart',
	'ShoppingBasket',
	'Store',
	'ChartPie',
	'ChartColumn',
	'TrendingUp',
	'TrendingDown',
	'BuildingComplex',
	'Warehouse',
	'House',
	'Hospital',
	'WashingMachine',
	'Armchair',
	'Bath',
	'Refrigerator',
	'Pill',
	'TestTube',
	'Bone',
	'Stethoscope',
	'Phone',
	'Key',
	'LockKeyhole',
	'Siren',
	'VenetianMask',
	'CircleCheck',
	'CircleX',
	'TriangleAlert',
	'Crown',
	'Pin',
	'Trash',
	'Cat',
	'Umbrella',
	'Ghost',
	'Flag',
	'Trophy',
	'Glasses',
	'Box',
	'Shirt',
	'Fuel',
	'Bus',
	'Motorbike',
	'Scooter',
	'Clock',
	'Calendar',
	'Search',
	'FaceSlightlySmiling',
	'FaceNeutral',
	'FaceSlightlyFrowning',
	'Gamepad2',
	'Smartphone',
	'Headphones',
	'Laptop',
	'Monitor',
	'Lightbulb',
	'Tag',
	'BadgePercent',
	'Euro',
	'DollarSign',
] as const satisfies readonly (keyof typeof icons)[];

export type IconName = (typeof iconNameList)[number];

export function IconFromName({ name, ...props }: { name: IconName } & LucideProps) {
	const Icon = icons[name];
	if (!Icon) return null;
	return <Icon {...props} />;
}

const RemoveIconButton = styled.div`
	position: absolute;
	top: -5px;
	right: -5px;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background: ${PrimaryColor};
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
`;

export async function compressImageToBase64(file: File, maxSize = 40): Promise<string> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		const objectUrl = URL.createObjectURL(file);
		img.onload = () => {
			URL.revokeObjectURL(objectUrl);
			const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
			const w = Math.round(img.width * scale);
			const h = Math.round(img.height * scale);
			const canvas = document.createElement('canvas');
			canvas.width = w;
			canvas.height = h;
			const ctx = canvas.getContext('2d')!;
			ctx.drawImage(img, 0, 0, w, h);
			resolve(canvas.toDataURL('image/webp', 0.5));
		};
		img.onerror = reject;
		img.src = objectUrl;
	});
}

export function DisplayIconOrImg({ iconString, imgStyle, iconStyle, iconSize, iconColor, dotSize }: {
	iconString: string | IconName,
	imgStyle?: object,
	iconStyle?: object,
	iconSize?: number,
	iconColor?: string,
	dotSize?: string
}) {
	return (
		iconString.startsWith('data:image')
			?
			<img src={iconString} style={imgStyle} />
			:
			<IconDotFromLabel
				dotSize={dotSize}
				label={iconString as IconName}
				iconStyle={iconStyle}
				iconSize={iconSize}
				color={iconColor}
				strokeWidth={2.5}
			/>
	);
}

function SelectIcon(props: {
	setPinStyleLogo: (logo: string | undefined) => void,
	selectedIcon?: string | IconName,
	color?: string
}) {
	const handleRemove = (e: React.MouseEvent) => {
		e.stopPropagation();
		props.setPinStyleLogo(undefined);
	};

	return (
		<div style={{ position: 'relative', display: 'inline-flex' }}>
			<SelectIconContainer iconSelected={!!props.selectedIcon}>
				{props.selectedIcon ?
					<DisplayIconOrImg
						iconString={props.selectedIcon}
						imgStyle={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
						iconColor={props.color}
					/>
					:
					<Images color={PrimaryColor} size={16} />
				}
			</SelectIconContainer>
			{props.selectedIcon && (
				<RemoveIconButton onClick={handleRemove}>
					<CloseIcon color='white' size={8} />
				</RemoveIconButton>
			)}
		</div>
	);
}

function IconDotFromLabel(props: {
	label: IconName,
	dotSize?: number | string,
	iconSize?: number,
	color?: string,
	strokeWidth?: number,
	iconStyle?: object
}) {
	return (
		<SelectIconContainer color={props.color} size={props.dotSize} style={props.iconStyle}>
			<IconFromName name={props.label} color={props.color} size={props.iconSize} strokeWidth={props.strokeWidth} />
		</SelectIconContainer>
	);
}

export function DropdownIcons(props: DropdownPropsNoData<string, false, false> & {
	optionDotSize?: number,
	dotSize?: string,
	optionRows?: number,
	optionCols?: number,
	selectedValue?: IconName | string,
	iconsList?: string[],
	setPinStyleLogo?: (logo: string | undefined) => void,
	iconColor?: string
}) {
	const inputRef = React.useRef<HTMLInputElement>(null);
	const iconsList = props?.iconsList ?? iconNameList;
	const iconsData = React.useMemo(() => {
		return iconsList.map((iconLabel) => ({
			label: <IconDotFromLabel label={iconLabel} dotSize={props.optionDotSize} iconSize={14} strokeWidth={2.5} color={props.iconColor} />,
			value: iconLabel
		}));
	}, [props.iconsList, props.iconColor]);

	const handleFileChange = async(e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		try {
			const base64 = await compressImageToBase64(file);
			props.setPinStyleLogo?.(base64);
		} catch {
			// ignore
		}
		e.target.value = '';
	};

	return <>
		<input
			ref={inputRef}
			type='file'
			accept='image/*'
			style={{ display: 'none' }}
			onChange={handleFileChange}
		/>
		<Dropdown<string>
			key={props.iconColor}
			{...props}
			before={<FlexDiv flow='column' align='stretch' gap='8px' padding='8px' fontSize='12px'>
				<FlexDiv flow='column' align='stretch' gap='4px'>
					<Translate id='images.load_image' />
					<DefaultButton margin='0' height='30px' width='130px' onClick={() => inputRef.current?.click()}>
						<FlexDiv gap='6px' justify='center'>
							<Images color='white' /><Translate id='images.add_image' />
						</FlexDiv>
					</DefaultButton>
				</FlexDiv>
				<Translate id="images.choose_icon" />
			</FlexDiv>}
			searchable={false}
			gallery
			selectedValue={props.selectedValue ? iconsData.find(c => c.value === props.selectedValue) : undefined}
			JSXButton={props.JSXButton ? props.JSXButton :
				() => <SelectIcon
					selectedIcon={props.selectedValue}
					setPinStyleLogo={props.setPinStyleLogo ?? (() => { })}
					color={props.iconColor}
				/>
			}
			dropdownStyle={{
				...props.dropdownStyle,
				optionHeight: props.dropdownStyle?.optionHeight ?? ((props.optionRows ?? DEFAULT_ROWS) * ((props.optionDotSize ?? 35) + 22)) + 'px',
				optionWidth: props.dropdownStyle?.optionWidth ?? ((props.optionCols ?? DEFAULT_COLUMNS) * ((props.optionDotSize ?? 35) + 22)) + 'px',
				optionPadding: '0'
			}}
			datalist={iconsData}
		/>
	</>;
}
